import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiCamera, FiCheckCircle, FiAlertCircle, FiLoader,
    FiUser, FiCpu, FiTablet, FiShield, FiUserCheck, FiX, FiMapPin, FiNavigation
} from 'react-icons/fi';
import { IoScan } from "react-icons/io5";
import './face.css';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useUser } from '../../contexts/UserContext';
import Layout from '../../components/Layout/Layout';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import * as turf from '@turf/turf';
import 'leaflet/dist/leaflet.css';

// Fix for Leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom Red Marker Icon
const RED_ICON = L.icon({
    iconUrl: 'https://cdn-icons-png.flaticon.com/512/684/684908.png',
    iconSize: [35, 35],
    iconAnchor: [17, 35],
    popupAnchor: [0, -35],
});

const FaceAttendance = () => {
    const { user } = useUser();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null); // Ref to track active stream robustly
    const [stream, setStream] = useState(null);
    const [mode, setMode] = useState('verify'); // verify | register
    const [status, setStatus] = useState('idle');
    const timerRef = useRef(null); // Ref for verification countdown
    const processingRef = useRef(false);
    const [capturedFrames, setCapturedFrames] = useState([]);
    const [livenessPrompt, setLivenessPrompt] = useState('Please position your face');
    const [result, setResult] = useState(null);
    const [location, setLocation] = useState({ lat: null, lng: null });
    const [geofences, setGeofences] = useState([]);
    const [selectedSite, setSelectedSite] = useState(null);
    const [isInside, setIsInside] = useState(false);
    const [distToSite, setDistToSite] = useState(0);
    const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]);
    const [mapZoom, setMapZoom] = useState(17);
    const [countdown, setCountdown] = useState(null);
    const [currentAction, setCurrentAction] = useState(null); // punchin | punchout
    const [isCameraReady, setIsCameraReady] = useState(false);
    const [isCounting, setIsCounting] = useState(false);
    const [punchType, setPunchType] = useState('IN'); // IN | OUT
    const [hasPunchedIn, setHasPunchedIn] = useState(false);
    const [isStatusLoading, setIsStatusLoading] = useState(true);
    const [regAgeWarning, setRegAgeWarning] = useState(false);
    const [faceResetNotice, setFaceResetNotice] = useState(false); // admin reset banner

    useEffect(() => {
        fetchGeofences();
        const watchId = navigator.geolocation.watchPosition((pos) => {
            const newLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            setLocation(newLoc);
            if (!selectedSite) setMapCenter([newLoc.lat, newLoc.lng]);
        }, (err) => console.error(err), { enableHighAccuracy: true });
        return () => navigator.geolocation.clearWatch(watchId);
    }, []);

    useEffect(() => {
        if (user?.user_id) {
            checkPunchStatus();
        }
    }, [user]);

    const checkPunchStatus = async () => {
        setIsStatusLoading(true);
        try {
            const res = await api.get(`/face/status?user_id=${user.user_id}`);
            if (res.data.success) {
                setHasPunchedIn(res.data.punched_in);

                const lsKey = `face_registered_${user.user_id}`;

                if (res.data.registration_date) {
                    // Face exists — store flag so we can detect future reset
                    localStorage.setItem(lsKey, '1');
                    setFaceResetNotice(false);

                    // Check registration age (90-day re-register prompt)
                    const regDate = new Date(res.data.registration_date);
                    const diffDays = Math.ceil(Math.abs(new Date() - regDate) / (1000 * 60 * 60 * 24));
                    if (diffDays > 90) setRegAgeWarning(true);
                } else {
                    // No face registered — was it reset by admin?
                    if (localStorage.getItem(lsKey) === '1') {
                        setFaceResetNotice(true); // show the admin-reset popup
                    }
                }
            }
        } catch (err) {
            console.error("Status check failed", err);
        } finally {
            setIsStatusLoading(false);
        }
    };

    const fetchGeofences = async () => {
        try {
            const res = await api.get('/geofence');
            if (res.data.status) {
                const parsed = res.data.data.map(f => ({
                    ...f,
                    polygon_coordinates: JSON.parse(f.polygon_coordinates)
                }));
                setGeofences(parsed);

                // Persist site selection
                const savedSiteId = localStorage.getItem('face_selected_site_id');
                if (savedSiteId) {
                    const savedSite = parsed.find(f => f.id == savedSiteId);
                    if (savedSite) {
                        setSelectedSite(savedSite);
                        setMapCenter([parseFloat(savedSite.centroid_lat), parseFloat(savedSite.centroid_lng)]);
                    }
                }
            }
        } catch (err) { console.error("Geofence load error", err); }
    };

    useEffect(() => {
        if (location.lat && selectedSite) {
            checkGeofence();
        }
    }, [location, selectedSite]);

    const checkGeofence = () => {
        try {
            if (!selectedSite) return;

            // Handle Suspended (Inactive) geofences: Bypass GPS check
            if (selectedSite.status === 'inactive') {
                setIsInside(true);
                setDistToSite(0);
                return;
            }

            // Normal enforcement logic for Active geofences
            // Leaflet uses [lat, lng], but Turf MUST have [lng, lat]
            const pt = turf.point([location.lng, location.lat]);

            // Transform polygon coordinates to [lng, lat] for Turf
            const turfPolyCoords = selectedSite.polygon_coordinates.map(c => [c[1], c[0]]);
            // Close the loop
            turfPolyCoords.push(turfPolyCoords[0]);

            const poly = turf.polygon([turfPolyCoords]);
            const inside = turf.booleanPointInPolygon(pt, poly);
            setIsInside(inside);

            if (!inside) {
                // Centroid coordinates are usually [lat, lng] in our DB, so swap for Turf
                const targetLng = parseFloat(selectedSite.centroid_lng);
                const targetLat = parseFloat(selectedSite.centroid_lat);
                const targetPt = turf.point([targetLng, targetLat]);

                const distanceMeters = turf.distance(pt, targetPt, { units: 'meters' });
                setDistToSite(Math.round(distanceMeters));
            } else {
                setDistToSite(0);
            }
        } catch (err) { console.error("Geofence check error", err); }
    };

    const handleSiteSelect = (e) => {
        const siteId = e.target.value;
        const site = geofences.find(f => f.id == siteId);
        setSelectedSite(site);
        if (site) {
            localStorage.setItem('face_selected_site_id', siteId);
            setMapCenter([parseFloat(site.centroid_lat), parseFloat(site.centroid_lng)]);
            setMapZoom(18);
        } else {
            localStorage.removeItem('face_selected_site_id');
        }
    };

    const RecenterMap = ({ center, zoom }) => {
        const map = useMap();
        useEffect(() => {
            if (center && center[0] && center[1]) {
                map.setView(center, zoom || map.getZoom());
            }
        }, [center, zoom, map]);
        return null;
    };

    useEffect(() => {
        // Cleanup effect to turn off camera when navigating away
        return () => {
            if (stream) {
                stream.getTracks().forEach(track => {
                    track.stop();
                    console.log("Camera track stopped");
                });
            }
        };
    }, [stream]);


    const startCamera = async (type = 'IN') => {
        if (status !== 'idle' && status !== 'error') return;

        // --- Clean up any lingering state from a previous attempt ---
        // This is critical: processingRef may still be true from a failed run,
        // and an old stream may still be open if the last attempt ended in error.
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) videoRef.current.srcObject = null;
        processingRef.current = false; // Always reset so countdown can start
        // --- End cleanup ---

        setCurrentAction(type === 'IN' ? 'punchin' : 'punchout');
        setPunchType(type);
        setStatus('initializing');
        setIsCameraReady(false);
        setIsCounting(false);
        setCountdown(null);

        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: 480, height: 480 }
            });

            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                await videoRef.current.play();
            }

            setStream(mediaStream);
            streamRef.current = mediaStream;
            setIsCameraReady(true);
            setStatus('scanning');
            setLivenessPrompt(mode === 'register' ? 'Position your face...' : 'Liveness Check: Hold Still');

            if (mode === 'register') {
                setTimeout(startRegistrationCollection, 1500);
            } else {
                // AUTO START after camera stabilization
                setTimeout(() => {
                    if (streamRef.current) {
                        runVerification(true, type);
                    }
                }, 1500);
            }
        } catch (err) {
            console.error("Camera access error:", err);
            setStatus('error');
            toast.error("Camera hardware not ready or permission denied");
            stopCamera('idle');
        }
    };


    const stopCamera = (newStatus = 'idle') => {
        // Clear all timers
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        setCountdown(null);
        setIsCounting(false);
        setCurrentAction(null);
        setIsCameraReady(false);

        // Stop hardware
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }

        setStatus(typeof newStatus === 'string' ? newStatus : 'idle');
        processingRef.current = false;
        setCapturedFrames([]);
    };

    const getFrameBlob = async () => {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        if (!video) return null;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        return new Promise(resolve => {
            canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.7);
        });
    };

    const startRegistrationCollection = async () => {
        const frames = [];
        const totalNeeded = 3;

        for (let i = 0; i < totalNeeded; i++) {
            setLivenessPrompt(`Capturing Sample ${i + 1}/${totalNeeded}...`);
            await new Promise(r => setTimeout(r, 800)); // Delay between shots
            const frame = await getFrameBlob();
            if (frame) {
                frames.push(frame);
                setCapturedFrames([...frames]);
            }
        }

        if (frames.length > 0) {
            stopCamera('processing');
            // We use the first high-quality frame for the main embedding
            submitRegistration(frames[0]);
        } else {
            toast.error("Failed to capture frames.");
            stopCamera('idle');
        }
    };

    const submitRegistration = async (frame) => {
        setStatus('processing');
        try {
            const formData = new FormData();
            formData.append('user_id', user.user_id);
            formData.append('image', frame, 'registration.jpg');

            const response = await api.post('/face/register', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                toast.success("Biometrics Registered Successfully!");
                setStatus('success');
                setResult({ type: 'registration' });
                // Mark as registered so admin-reset can be detected later
                localStorage.setItem(`face_registered_${user.user_id}`, '1');
                setFaceResetNotice(false);
            }
        } catch (err) {
            const serverMsg = err.response?.data?.message || err.response?.data?.messages?.error;
            toast.error(serverMsg || "Registration failed.");
            setStatus('error');
        } finally {
            stopCamera();
        }
    };

    const runVerification = async (isAuto = false, explicitPunchType = null) => {
        // Clear any existing timer before starting new one
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }

        // Validation
        if (!isAuto && status !== 'scanning') return;
        if (processingRef.current || !streamRef.current) return;

        const targetPunchType = explicitPunchType || punchType;

        setIsCounting(true);
        setCountdown(3);

        timerRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    timerRef.current = null;
                    setCountdown(null);
                    setIsCounting(false);
                    startLivenessCapture(targetPunchType);
                    return null;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const startLivenessCapture = async (activePunchType = null) => {
        if (processingRef.current) return;
        processingRef.current = true;
        setLivenessPrompt("Scanning... Stay Still");

        const finalPunchType = activePunchType || punchType;

        try {
            // Capture 2 frames 400ms apart for liveness check
            const frames = [];
            for (let i = 0; i < 2; i++) {
                const frame = await getFrameBlob();
                if (frame) frames.push(frame);
                await new Promise(r => setTimeout(r, 400));
            }

            if (frames.length < 2) throw new Error("Capture failed — ensure camera is unobstructed");

            // Stop hardware immediately after capture so camera LED turns off
            // and the device is free for the next punch attempt
            stopCamera('processing');

            const formData = new FormData();
            formData.append('user_id_hint', user?.user_id);
            formData.append('image1', frames[0], 'frame1.jpg');
            formData.append('image2', frames[1], 'frame2.jpg');
            formData.append('punch_type', finalPunchType);
            if (selectedSite?.id) formData.append('geofence_id', selectedSite.id);
            if (location.lat) formData.append('gps_lat', location.lat);
            if (location.lng) formData.append('gps_lng', location.lng);

            const response = await api.post('/face/verify', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success) {
                const resultPunchType = response.data.punched_in ? 'IN' : 'OUT';
                setResult({ ...response.data.user, confidence: response.data.confidence, punch_type: resultPunchType });
                setStatus('success');
                setHasPunchedIn(response.data.punched_in);
                toast.success("Attendance Verified!");
            } else {
                setStatus('error');
                const confidence = response.data.confidence ? ` (Conf: ${(response.data.confidence * 100).toFixed(1)}%)` : '';
                const errMsg = (response.data.message || "Verification Failed") + confidence;
                setLivenessPrompt(errMsg);
                toast.error(errMsg);
            }
        } catch (err) {
            const serverMsg = err.response?.data?.message || err.message || "Biometric Service Timeout";
            const confidence = err.response?.data?.confidence ? ` (Conf: ${(err.response?.data?.confidence * 100).toFixed(1)}%)` : '';
            setStatus('error');
            toast.error(serverMsg + confidence);
        } finally {
            // CRITICAL: always unlock so the next punch attempt can run
            processingRef.current = false;
        }
    };

    return (
        <Layout noSidebar={true}>
            <div className="face-container">
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="support-panel">
                    <div className="flex justify-between items-center border-b pb-4 mb-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                                <IoScan className="text-red-600" /> Face Attendance System
                            </h2>
                            <p className="text-gray-500 text-sm">Official Employee Authentication Portal</p>
                            {regAgeWarning && mode === 'verify' && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mt-2 bg-amber-50 text-amber-700 p-2 rounded-lg text-[10px] font-bold border border-amber-200 flex items-center gap-2"
                                >
                                    <FiAlertCircle className="text-sm" />
                                    Biometrics are 90 days old. Please re-register for better accuracy.
                                </motion.div>
                            )}
                        </div>
                        <div className="flex flex-mobile-stack gap-2">
                            <button
                                onClick={() => { setMode('verify'); stopCamera(); }}
                                className={`px-4 py-2 rounded-md text-sm font-bold ${mode === 'verify' ? 'bg-red-600 text-white' : 'bg-gray-100'}`}
                            >
                                Verification
                            </button>
                            <button
                                onClick={() => { setMode('register'); stopCamera(); }}
                                className={`px-4 py-2 rounded-md text-sm font-bold ${mode === 'register' ? 'bg-red-600 text-white' : 'bg-gray-100'}`}
                            >
                                Register Face
                            </button>
                        </div>
                    </div>

                    <AnimatePresence mode="wait">
                        {status === 'idle' && (
                            <motion.div key="idle" className="text-center py-12">
                                {mode === 'register' ? (
                                    <>
                                        <FiUser className="text-6xl mx-auto mb-4 text-gray-300" />
                                        <h3 className="text-xl font-semibold mb-2">Welcome, {user?.first_name}</h3>
                                        <p className="text-gray-500 mb-6 max-w-md mx-auto">
                                            Ensure your face is clearly visible and within the frame for highest accuracy (99%).
                                        </p>

                                        <div className="employee-info-card max-w-sm mx-auto mb-6 text-center">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <span className="info-label">Employee ID</span>
                                                    <span className="info-value text-red-600">{user?.emp_id || 'U' + user?.user_id}</span>
                                                </div>
                                                <div>
                                                    <span className="info-label">Designation</span>
                                                    <span className="info-value">{user?.designation_name || 'Staff'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <button onClick={startCamera} className="btn-red mx-auto">
                                            <FiCamera /> Open Camera & Start
                                        </button>
                                    </>
                                ) : (
                                    <div className="geofence-selection min-h-[400px]">
                                        <div className="flex flex-col md:flex-row gap-6">
                                            {/* Left: Site Info & Map */}
                                            <div className="flex-1">
                                                <div className="form-group mb-4">
                                                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block text-left">
                                                        Assigned Site Deployment
                                                    </label>
                                                    <div className="relative">
                                                        <select
                                                            value={selectedSite?.id || ""}
                                                            onChange={handleSiteSelect}
                                                            className="w-full p-3 pl-11 bg-gray-50 border border-gray-200 rounded-xl text-sm font-semibold outline-none focus:ring-2 focus:ring-red-500 transition-all cursor-pointer"
                                                        >
                                                            <option value="">-- Choose Work Site --</option>
                                                            {geofences.map(f => (
                                                                <option key={f.id} value={f.id}>{f.customer_name}</option>
                                                            ))}
                                                        </select>
                                                        <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                                    </div>
                                                </div>

                                                <div className="map-preview-employee relative">
                                                    <MapContainer
                                                        center={mapCenter}
                                                        zoom={mapZoom}
                                                        className="site-map-emp"
                                                        zoomControl={false}
                                                    >
                                                        <TileLayer url="https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}" subdomains={['mt0', 'mt1', 'mt2', 'mt3']} />
                                                        <RecenterMap center={mapCenter} zoom={mapZoom} />

                                                        {location.lat && location.lng && (
                                                            <Marker position={[location.lat, location.lng]}>
                                                                <Popup>My Current Position</Popup>
                                                            </Marker>
                                                        )}

                                                        {geofences.map(f => (
                                                            <Marker key={f.id} position={[f.centroid_lat, f.centroid_lng]} icon={RED_ICON}>
                                                                <Popup>{f.customer_name}</Popup>
                                                            </Marker>
                                                        ))}

                                                        {selectedSite && (
                                                            <Polygon
                                                                positions={selectedSite.polygon_coordinates}
                                                                pathOptions={{ color: isInside ? '#10b981' : '#ef4444', fillOpacity: 0.3 }}
                                                            />
                                                        )}
                                                    </MapContainer>
                                                    <button
                                                        className="emp-locate-btn"
                                                        onClick={() => {
                                                            if (location.lat) {
                                                                setMapCenter([location.lat, location.lng]);
                                                                setMapZoom(19); // High zoom
                                                            }
                                                        }}
                                                        title="Center My Location"
                                                    >
                                                        <FiNavigation />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Right: Status & Action */}
                                            <div className="w-full md:w-80 flex flex-col justify-center border-l pl-0 md:pl-6">
                                                <div className="employee-info-card mb-6">
                                                    <div className="grid grid-cols-1 gap-3 text-left">
                                                        <div>
                                                            <span className="info-label">Employee ID</span>
                                                            <span className="info-value text-red-600">{user?.emp_id || 'U' + user?.user_id}</span>
                                                        </div>
                                                        <div>
                                                            <span className="info-label">Designation</span>
                                                            <span className="info-value">{user?.designation_name || 'Staff'}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={`status-pill mb-6 ${selectedSite?.status === 'inactive' ? 'bg-amber-50 text-amber-700' : isInside ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                                                    {selectedSite ? (
                                                        selectedSite.status === 'inactive' ? (
                                                            <span className="flex items-center gap-2 font-bold text-sm">
                                                                <FiAlertCircle /> Enforcement Suspended (GPS Bypass)
                                                            </span>
                                                        ) : isInside ? (
                                                            <span className="flex items-center gap-2 font-bold text-sm">
                                                                <FiCheckCircle /> Site Perimeter Verified
                                                            </span>
                                                        ) : (
                                                            <div className="text-center">
                                                                <span className="flex items-center justify-center gap-2 font-bold text-sm">
                                                                    <FiAlertCircle /> Outside Site Boundary
                                                                </span>
                                                                <p className="text-[10px] mt-1 opacity-80 uppercase tracking-tighter">
                                                                    ~{distToSite >= 1000 ? (distToSite / 1000).toFixed(2) + " km" : distToSite + " meters"} to target
                                                                </p>
                                                            </div>
                                                        )
                                                    ) : (
                                                        <span className="flex items-center gap-2 font-bold text-sm">
                                                            <FiNavigation /> Awaiting Site Selection
                                                        </span>
                                                    )}
                                                </div>

                                                <div className="flex gap-4">
                                                    {isStatusLoading ? (
                                                        <div className="flex-1 py-4 bg-gray-50 rounded-xl flex items-center justify-center gap-2 text-gray-400 font-bold animate-pulse">
                                                            <FiLoader className="animate-spin" /> Verifying Status...
                                                        </div>
                                                    ) : !hasPunchedIn ? (
                                                        <button
                                                            onClick={() => startCamera('IN')}
                                                            disabled={!isInside}
                                                            className={`flex-1 py-4 rounded-xl flex items-center justify-center gap-3 font-bold transition-all shadow-lg ${isInside
                                                                ? 'bg-green-600 text-white hover:bg-green-700 hover:scale-[1.02]'
                                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                }`}
                                                        >
                                                            <FiCamera className="text-xl" /> Punch In
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => startCamera('OUT')}
                                                            disabled={!isInside}
                                                            className={`flex-1 py-4 rounded-xl flex items-center justify-center gap-3 font-bold transition-all shadow-lg ${isInside
                                                                ? 'bg-red-600 text-white hover:bg-red-700 hover:scale-[1.02]'
                                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                }`}
                                                        >
                                                            <FiCamera className="text-xl" /> Punch Out
                                                        </button>
                                                    )}
                                                </div>
                                                {!isInside && selectedSite && (
                                                    <p className="text-[10px] text-red-500 mt-2 font-medium">Please enter the verified perimeter to proceed.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {(status === 'scanning' || status === 'processing' || status === 'initializing') && (
                            <motion.div key="camera" className="text-center">
                                <div className="liveness-hint mb-4">
                                    {status === 'initializing' ? 'Waking AI Engine...' : livenessPrompt}
                                </div>

                                <div className="camera-section mx-auto shadow-2xl overflow-hidden">
                                    <video ref={videoRef} autoPlay playsInline muted className="camera-video" />
                                    <div className="scan-overlay-v2">
                                        <div className="face-guide"></div>
                                    </div>
                                    {status === 'processing' && (
                                        <div className="absolute inset-0 bg-white/60 flex items-center justify-center backdrop-blur-sm z-30">
                                            <div className="text-center">
                                                <FiLoader className="text-4xl animate-spin text-red-600 mx-auto mb-2" />
                                                <p className="font-bold text-red-600">Neural Analysis In Progress...</p>
                                            </div>
                                        </div>
                                    )}
                                    {isCounting && countdown !== null && (
                                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-[2px] z-50">
                                            <motion.div
                                                initial={{ scale: 0.5, opacity: 0 }}
                                                animate={{ scale: 1.5, opacity: 1 }}
                                                key={countdown}
                                                className="text-8xl font-black text-white drop-shadow-2xl"
                                            >
                                                {countdown}
                                            </motion.div>
                                            <div className="mt-4 px-6 py-2 bg-white/20 backdrop-blur-md rounded-full text-white font-bold uppercase tracking-widest text-[10px]">
                                                {currentAction === 'punchin' ? 'Capturing Punch In' : 'Capturing Punch Out'}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {mode === 'register' ? (
                                    <div className="max-w-md mx-auto mt-4">
                                        <p className="text-xs text-gray-500 uppercase font-bold text-left mb-1">Capture Progress ({capturedFrames.length}/3)</p>
                                        <div className="progress-capture">
                                            <div className="progress-bar" style={{ width: `${(capturedFrames.length / 3) * 100}%` }}></div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                                        <button onClick={() => stopCamera()} className="btn-outline">
                                            <FiX className="inline mr-1" /> Cancel
                                        </button>
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {status === 'success' && (
                            <motion.div key="success" className="text-center py-10">
                                <FiCheckCircle className="text-7xl text-green-500 mx-auto mb-4" />
                                <h2 className="text-3xl font-bold mb-2">Biometric Identity Confirmed</h2>

                                <div className="attendance-status max-w-sm mx-auto mb-6">
                                    <FiUserCheck className="inline mr-2" /> {result?.punch_type === 'IN' ? 'PUNCH IN RECORDED' : 'PUNCH OUT RECORDED'}
                                </div>

                                <div className="employee-info-card max-w-sm mx-auto border-l-4 border-green-500">
                                    <p className="info-label">Matched Profile</p>
                                    <p className="text-2xl font-bold">{result?.name || user?.first_name}</p>
                                    <div className="grid grid-cols-2 mt-4 text-left">
                                        <div>
                                            <span className="info-label">Employee ID</span>
                                            <span className="info-value text-red-600">{result?.emp_id || user?.emp_id || user?.user_id}</span>
                                        </div>
                                        <div>
                                            <span className="info-label">Device</span>
                                            <span className="info-value">Verified Web</span>
                                        </div>
                                    </div>
                                </div>

                                <button onClick={() => setStatus('idle')} className="btn-red mx-auto mt-8">
                                    Back to Main Menu
                                </button>
                            </motion.div>
                        )}

                        {status === 'error' && (
                            <motion.div key="error" className="text-center py-10">
                                <FiAlertCircle className="text-7xl text-red-500 mx-auto mb-4" />
                                <h2 className="text-2xl font-bold mb-4">Face Verification Unsuccessful</h2>
                                <p className="text-gray-500 mb-8">The system could not verify your identity with high confidence. Please ensure good lighting and look directly at the camera.</p>
                                <div className="flex gap-4 justify-center">
                                    <button onClick={() => startCamera(punchType)} className="btn-red">Retry Capture</button>
                                    <button onClick={() => setStatus('idle')} className="btn-outline">Home</button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Footer icons removed as per request */}
                </motion.div>

                <canvas ref={canvasRef} style={{ display: 'none' }} />

                {/* Admin-Reset Notice Popup Overlay */}
                <AnimatePresence>
                    {faceResetNotice && (
                        <motion.div
                            key="reset-notice-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-md p-4"
                        >
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                                animate={{ scale: 1, opacity: 1, y: 0 }}
                                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                                className="bg-white rounded-2xl shadow-2xl p-8 max-w-sm w-full relative text-center"
                            >
                                <button
                                    onClick={() => {
                                        setFaceResetNotice(false);
                                        localStorage.removeItem(`face_registered_${user.user_id}`);
                                    }}
                                    className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 transition-colors"
                                >
                                    <FiX className="w-4 h-4" />
                                </button>

                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <FiAlertCircle className="w-8 h-8 text-red-600" />
                                </div>

                                <h3 className="text-lg font-bold text-gray-800 mb-2">Biometrics Reset</h3>
                                <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                                    Your face data has been <span className="font-bold text-red-600">reset by the administrator</span>.
                                    Please register again to continue using face attendance.
                                </p>

                                <button
                                    onClick={() => {
                                        setFaceResetNotice(false);
                                        localStorage.removeItem(`face_registered_${user.user_id}`);
                                        setMode('register');
                                        stopCamera();
                                    }}
                                    className="btn-red w-full justify-center py-3 text-base"
                                >
                                    <FiCamera /> Re-register Now
                                </button>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </Layout>
    );
};

export default FaceAttendance;
